import { HttpParams } from '@angular/common/http';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { FilterGroupComponent } from '@app/components/filter-group/filter-group.component';
import { IconComponent } from '@app/components/icon/icon.component';
import { LoadingSpinnerComponent } from '@app/components/loading-spinner/loading-spinner.component';
import { MultiLinkAccordionComponent } from '@app/components/multi-link-accordion/multi-link-accordion.component';
import { PageErrorComponent } from '@app/components/page-error/page-error.component';
import { ContextualizedSelectsFilterConfig } from '@app/models/filters/contextualized-selects-filter-config';
import { ClassAmmFilterMapper, FilterMapper } from '@app/models/filters/filter-mapper';
import { FilterStatus } from '@app/models/filters/filter-status';
import * as FilterType from '@app/models/filters/filter-type';
import { GenericFilterConfig } from '@app/models/filters/generic-filter-config';
import { PageConfig } from '@app/models/page-config';
import { ApiCallerService } from '@app/services/api-caller.service';
import { RouterService } from '@app/services/router.service';
import { saveAs } from 'file-saver';
import { CallToActionBtnComponent } from './call-to-action-btn/call-to-action-btn.component';

@Component({
	selector: 'app-ars-view',
	standalone: true,
	imports: [
		MultiLinkAccordionComponent,
		FilterGroupComponent,
		IconComponent,
		CallToActionBtnComponent,
		LoadingSpinnerComponent,
		PageErrorComponent,
	],
	templateUrl: './ars-view.component.html',
	styleUrl: './ars-view.component.css',
})
export class ARSViewComponent {
	pageConfig: PageConfig = {} as PageConfig;
	filtersStatus: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } } = {};
	visibleFilters: GenericFilterConfig[] = [];
	visibleFiltersIds = [
		'provinceComuni',
		'province',
		'comuni',
		'classAmm',
		'nomeStrada',
		'numStrada',
		'limPortata',
		'limSagoma',
		'limTipologia',
	];
	contentLoaded: boolean = false;

	loadingVisualizeReport: boolean = false;
	loadingDownloadPdfReport: boolean = false;
	loadingDownloadXlsReport: boolean = false;
	showReportError: boolean = false;
	showPageError: boolean = false;

	filterMapper: FilterMapper = new ClassAmmFilterMapper();

	constructor(
		private routerService: RouterService,
		private router: Router,
		private apiCallerService: ApiCallerService,
	) {}

	ngOnInit() {
		this.routerService.setActivePage(this.router.url);
		this.apiCallerService.getARSPageConfig(
			(data: PageConfig) => {
				this.pageConfig = data;
				this.initFilterStatus();
				this.setVisibleFilters();
			}, // next
			() => {
				// error
				this.showPageError = true;
			},
			() => (this.contentLoaded = true), // complete
		);
	}

	private initFilterStatus = () => {
		for (const filter of this.pageConfig.filters) {
			switch (filter.type) {
				// for a contextualized filter, we need to init its children in the filtersStatus
				// we're not tracking the selected values at parent level
				case FilterType.CONTEXTUALIZED_SELECTS:
					let casted = filter as ContextualizedSelectsFilterConfig;
					this.filtersStatus[casted.subfilters.first.id] = {
						selectedSecondaryValues: casted.subfilters.first.defaultValues,
						selectedPrimaryValues: casted.subfilters.first.defaultValues,
					};

					this.filtersStatus[casted.subfilters.second.id] = {
						selectedSecondaryValues: casted.subfilters.second.defaultValues,
						selectedPrimaryValues: casted.subfilters.second.defaultValues,
					};
					break;
				default:
					this.filtersStatus[filter.id] = {
						selectedPrimaryValues: [...filter.defaultValues],
						selectedSecondaryValues: [...filter.defaultValues],
					};
					break;
			}
		}
	};

	/**
	 * Sets visible filters checking against visibleFiltersIds array
	 */
	setVisibleFilters() {
		this.visibleFilters = [...this.pageConfig.filters.filter((f) => this.visibleFiltersIds.includes(f.id))];
	}

	/**
	 * Updates the filters status map with the new status received from the filter that changed.
	 * @param newStatus
	 */
	setNewFilterStatus = (newStatus: FilterStatus) => {
		this.filtersStatus = {
			...this.filtersStatus,
			[newStatus.filterId]: {
				selectedPrimaryValues: newStatus.selectedPrimaryValues,
				selectedSecondaryValues: newStatus.selectedSecondaryValues,
			},
		};

		// console.log('ARSComponent.setNewFilterStatus: ', this.filtersStatus);
	};

	/**
	 * Prepares GET query params and sends GET request to /report. At response received, opens a new window with pdf report.
	 */
	visualizeReport = () => {
		this.showReportError = false;
		let params = this.loadReportParams('ARS', 'pdf');
		this.loadingVisualizeReport = true;
		this.apiCallerService.getARSReport(
			params,
			(data: Blob) => {
				this.loadingVisualizeReport = false;
				this.openNewWindow(data);
			},
			() => {
				this.loadingVisualizeReport = false;
				this.showReportError = true;
			},
		);
	};

	/**
	 * Prepares GET query params and sends GET request to /report. At response received, report download as PDF is started.
	 */
	downloadPdfReport = () => {
		this.showReportError = false;
		let params = this.loadReportParams('ARS', 'pdf');

		this.loadingDownloadPdfReport = true;
		this.apiCallerService.getARSReport(
			params,
			(data: Blob) => {
				// next
				this.loadingDownloadPdfReport = false;
				saveAs(data, 'Trasporti Eccezionali.pdf');
			},
			() => {
				// error
				this.loadingDownloadPdfReport = false;
				this.showReportError = true;
			},
		);
	};

	/**
	 * Prepares GET query params and sends GET request to /report. At response received, report download as XLS is started.
	 */
	downloadXlsReport = () => {
		this.showReportError = false;
		let params = this.loadReportParams('ARS', 'xls');

		this.loadingDownloadXlsReport = true;
		this.apiCallerService.getARSReport(
			params,
			(data: Blob) => {
				// next
				this.loadingDownloadXlsReport = false;
				saveAs(data, 'Trasporti Eccezionali.xls');
			},
			() => {
				// error
				this.loadingDownloadXlsReport = false;
				this.showReportError = true;
			},
		);
	};

	/**
	 * Prepares params for report generation
	 * @param reportType
	 * @param reportFormat 'pdf' or 'xls'
	 */
	private loadReportParams = (reportType: string, reportFormat: string) => {
		let params = new HttpParams();
		// restrict params to the ones handled by ARS
		// the visibleFilterIds specifies a list of filter ids that are the active ones
		// we filter the filterStatus keys to match that list (e.g. keep out limPuntuali)
		let ids = Object.keys(this.filtersStatus).filter((k) => this.visibleFiltersIds.includes(k));
		for (const id of ids) {
			const values = this.filtersStatus[id].selectedPrimaryValues;

			for (const value of values) {
				params = params.append(id, value);
			}
		}

		params = params.append('reportType', reportType);
		params = params.append('reportFormat', reportFormat);

		return params;
	};

	/**
	 * Opens new window and displays content of passed Blob
	 * @param data
	 */
	private openNewWindow = (data: Blob) => {
		const url = window.URL.createObjectURL(data);
		window.open(url, '_blank', 'height=600, width=1000');
	};
}
